/* ============================================================
   Formatação, em pt-BR

   Mora no core porque um número formatado de dois jeitos diferentes em
   duas telas é um bug de confiança: a pessoa vê "0,3" no prato e "0,25"
   no placar e conclui, com razão, que a conta está errada.
   ============================================================ */

/**
 * 1.5 → "1,5" · 3 → "3" · 0.25 → "0,25"
 *
 * Duas casas só quando a segunda diz alguma coisa. Arredondar em uma casa
 * fixa era o comportamento antigo e mentia com peso por refeição: uma
 * refeição de peso 0,25 aparecia como "0,3" na tela e valia 0,25 no placar.
 */
export function formatPoints(n: number): string {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  if (Number.isInteger(v)) return String(v);
  const decimals = Math.round(v * 10) / 10 === v ? 1 : 2;
  return v.toFixed(decimals).replace(".", ",");
}

/** "1 pt" · "2 pts" · "0,25 pts" — o singular só vale para o 1 exato. */
export function pointsLabel(n: number): string {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return `${formatPoints(v)} ${v === 1 ? "pt" : "pts"}`;
}

/** "1.240 kcal" */
export function formatKcal(n: number): string {
  return `${Math.round(Number(n) || 0).toLocaleString("pt-BR")} kcal`;
}

export function formatMoney(n: number): string {
  const v = Number(n);
  if (!isFinite(v)) return "";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** Nota média com uma casa; "—" quando ninguém votou. */
export function formatRating(n: number | null): string {
  if (n == null || !isFinite(n)) return "—";
  return (Math.round(n * 10) / 10).toFixed(1).replace(".", ",");
}

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/**
 * Duas `DayKey` viram um intervalo legível em português.
 *
 * Existe porque o placar e o recap mostravam `2026-09-06 → 2026-09-12`
 * na cara de quem usa: o `DayKey` é o formato interno com que o core
 * compara dias, e serve para isso — não para ser lido.
 *
 * O ano só aparece quando não é o de referência, pelo mesmo motivo que
 * o feed escreve "Hoje" em vez da data: repetir o ano corrente em toda
 * tela é ruído. Quem chama passa `anoAtual`; a função continua pura, que
 * é o que a deixa testável sem congelar o relógio.
 */
export function formatIntervalo(de: string, ate: string, anoAtual?: number): string {
  const parse = (s: string) => {
    const [a, m, d] = String(s).split("-").map(Number);
    return a && m && d && m >= 1 && m <= 12 ? { a, m, d } : null;
  };
  const i = parse(de);
  const f = parse(ate);
  // Sem data válida não dá para inventar rótulo: devolve o que veio, que
  // pelo menos é inspecionável, em vez de "NaN de undefined".
  if (!i || !f) return de === ate ? de : `${de} → ${ate}`;

  // Intervalo invertido é erro de quem chamou, mas mostrar "12 a 6" seria
  // pior que mostrar o intervalo certo ao contrário.
  const [ini, fim] = i.a * 10000 + i.m * 100 + i.d <= f.a * 10000 + f.m * 100 + f.d
    ? [i, f] : [f, i];

  const mes = (x: { m: number }) => MESES[x.m - 1];
  const comAno = (x: { a: number }) => x.a !== anoAtual ? ` de ${x.a}` : "";

  if (ini.a === fim.a && ini.m === fim.m && ini.d === fim.d) {
    return `${ini.d} de ${mes(ini)}${comAno(ini)}`;
  }
  if (ini.a !== fim.a) {
    return `${ini.d} de ${mes(ini)} de ${ini.a} a ${fim.d} de ${mes(fim)} de ${fim.a}`;
  }
  if (ini.m === fim.m) {
    return `${ini.d} a ${fim.d} de ${mes(fim)}${comAno(fim)}`;
  }
  return `${ini.d} de ${mes(ini)} a ${fim.d} de ${mes(fim)}${comAno(fim)}`;
}
